import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Phone, Send, ArrowRight } from 'lucide-react';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setIsSubscribing(true);

    try {
      const payload = new FormData();
      payload.append('access_key', '0057fa6e-08f0-4fa9-a8ae-703a52ee97d5');
      payload.append('subject', 'CredFlexi Newsletter Signup');
      payload.append('from_name', 'CredFlexi Website');
      payload.append('email', email);

      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: payload,
      });

      const data = await response.json();

      if (data.success) {
        alert('Thank you for subscribing!');
        setEmail('');
      } else {
        throw new Error(data.message || 'Failed to subscribe');
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      alert('Sorry, there was an error. Please try again.');
    } finally {
      setIsSubscribing(false);
    }
  };

  const solutions = [
    { name: 'OEM Dropship Enablement', href: '/solutions#oem' },
    { name: 'SMB-to-Enterprise Integration', href: '/solutions#smb' },
    { name: 'Financial Orchestration', href: '/solutions#financial' },
    { name: 'Insurance Integration', href: '/solutions#insurance' }
  ];

  const products = [
    { name: 'Agent-Driven Platform', href: '/products#platform' },
    { name: 'Marketplace Creation', href: '/products#marketplace' },
    { name: 'Analytics & Intelligence', href: '/products#analytics' },
    { name: 'Financial Tools', href: '/products#financial' }
  ];

  const resources = [
    { name: 'Documentation', href: '/resources#docs' },
    { name: 'Case Studies', href: '/resources#case-studies' },
    { name: 'Whitepapers', href: '/resources#whitepapers' },
    { name: 'Blog', href: '/resources#blog' }
  ];

  const company = [
    { name: 'About Us', href: '/about' },
    { name: 'Careers', href: '/about#careers' },
    { name: 'Contact', href: '/contact' }
  ];

  return (
    <footer className="bg-gray-900 text-white">
      {/* Newsletter Section */}
      <div className="border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="lg:flex lg:items-center lg:justify-between">
            <div>
              <h3 className="text-2xl font-bold text-white mb-2">
                Stay ahead of enterprise commerce trends
              </h3>
              <p className="text-gray-400 text-lg">
                Get insights, updates, and industry trends delivered to your inbox.
              </p>
            </div>
            <div className="mt-6 lg:mt-0 lg:ml-8">
              <form onSubmit={handleNewsletterSubmit} className="sm:flex max-w-md">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full px-4 py-3 rounded-l-lg bg-gray-800 border border-gray-700 text-white placeholder-gray-400 focus:ring-2 focus:ring-[#854DFF]/50 focus:border-transparent transition-colors duration-200"
                  required
                />
                <button
                  type="submit"
                  disabled={isSubscribing}
                  className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-[#854DFF] to-[#7A42E6] text-white font-semibold rounded-r-lg hover:from-[#7A42E6] hover:to-[#6F37CC] focus:ring-2 focus:ring-[#854DFF]/50 transition-all duration-200 disabled:opacity-50 flex items-center justify-center"
                >
                  {isSubscribing ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <>
                      Subscribe
                      <Send className="ml-2 h-4 w-4" />
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
          {/* Company Info */}
          <div className="col-span-2">
            <div className="flex items-center space-x-3 mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_flexicred-platform/artifacts/62e5fd61_Transparent%20Logo.png" 
                alt="CredFlexi Logo" 
                className="h-20 w-auto"
              />
            </div>
            <p className="text-gray-400 text-base leading-relaxed mb-6 max-w-md">
              The world's most advanced Agent-Driven Commerce Platform for OEMs, Financial Institutions, and Enterprise Organizations.
            </p>
            
            {/* Contact Info */}
            <div className="space-y-3">
              <div className="flex items-center space-x-3 text-gray-400">
                <Mail className="h-4 w-4 text-[#854DFF]" />
                <a href="mailto:info@credflexi.com" className="hover:text-white transition-colors duration-200">
                  info@credflexi.com
                </a>
              </div>
            </div>
          </div>

          {/* Solutions */}
          <div>
            <h3 className="text-white text-sm font-semibold uppercase tracking-wider mb-4">Solutions</h3>
            <ul className="space-y-2">
              {solutions.map((item) => (
                <li key={item.name}>
                  <Link to={item.href} className="text-gray-400 hover:text-white transition-colors duration-200 text-sm">
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Products */}
          <div>
            <h3 className="text-white text-sm font-semibold uppercase tracking-wider mb-4">Products</h3>
            <ul className="space-y-2">
              {products.map((item) => (
                <li key={item.name}>
                  <Link to={item.href} className="text-gray-400 hover:text-white transition-colors duration-200 text-sm">
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-white text-sm font-semibold uppercase tracking-wider mb-4">Company</h3>
            <ul className="space-y-2 mb-6">
              {company.map((item) => (
                <li key={item.name}>
                  <Link to={item.href} className="text-gray-400 hover:text-white transition-colors duration-200 text-sm">
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
            
            <h4 className="text-white text-sm font-semibold uppercase tracking-wider mb-4">Resources</h4>
            <ul className="space-y-2">
              {resources.map((item) => (
                <li key={item.name}>
                  <Link to={item.href} className="text-gray-400 hover:text-white transition-colors duration-200 text-sm">
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Global Offices */}
        <div className="mt-12 pt-8 border-t border-gray-800">
          <h3 className="text-white text-lg font-semibold mb-6">Global Enterprise Centers</h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="flex items-start space-x-3">
              <MapPin className="h-5 w-5 text-[#854DFF] mt-1 flex-shrink-0" />
              <div>
                <h4 className="text-white font-medium">North America</h4>
                <p className="text-gray-400 text-sm mt-1">
                  Atlanta - HQ<br />
                  Delaware
                </p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <MapPin className="h-5 w-5 text-[#854DFF] mt-1 flex-shrink-0" />
              <div>
                <h4 className="text-white font-medium">Europe</h4>
                <p className="text-gray-400 text-sm mt-1">
                  London
                </p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <MapPin className="h-5 w-5 text-[#854DFF] mt-1 flex-shrink-0" />
              <div>
                <h4 className="text-white font-medium">Asia-Pacific</h4>
                <p className="text-gray-400 text-sm mt-1">
                  Kolkata
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="md:flex md:items-center md:justify-between">
            <p className="text-gray-400 text-sm">
              © 2025 CredFlexi. All rights reserved.
            </p>
            <div className="mt-4 md:mt-0 flex space-x-6">
              <Link to="/privacy" className="text-gray-400 hover:text-white text-sm transition-colors duration-200">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-gray-400 hover:text-white text-sm transition-colors duration-200">
                Terms of Service
              </Link>
              <Link to="/security" className="text-gray-400 hover:text-white text-sm transition-colors duration-200">
                Security
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
